import { t } from '@i18n';
import { FormHelperText, Grid } from '@mui/material';
import { TARGET_PATH_FIRST_YEAR, TARGET_PATH_LAST_YEAR } from '@predium/client-lookup';
import { UnitsValue } from '@predium/utils';
import { isNil } from 'lodash';
import isEqual from 'lodash/isEqual';
import { memo, useEffect } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { useBuildingFilters } from '@/components/building-filters/BuildingFiltersProvider';
import NumberFilterInput, { NumberInputsSearchParams } from '@/components/building-filters/Filters/NumberFilterInput';
import Collapsible from '@/components/common/Collapsible';

const validateMinMax = (
  isStrandingYearsFiler: boolean,
  minFilter: number | null,
  maxFilter: number | null,
): string | undefined => {
  if (!isNil(minFilter) && !isNil(maxFilter)) {
    if (minFilter > maxFilter) {
      return t('Validation_NumberMinBiggerThanMax');
    }
  }

  if (minFilter !== null && minFilter < 0) {
    return t('Validation_NumberMin', { min: 0 });
  }

  if (maxFilter !== null && maxFilter < 0) {
    return t('Validation_NumberMin', { min: 0 });
  }

  if (isStrandingYearsFiler) {
    if (
      (minFilter && (minFilter < TARGET_PATH_FIRST_YEAR || minFilter > TARGET_PATH_LAST_YEAR)) ||
      (maxFilter && (maxFilter < TARGET_PATH_FIRST_YEAR || maxFilter > TARGET_PATH_LAST_YEAR))
    ) {
      return t('Validation_YearBetween', {
        min: TARGET_PATH_FIRST_YEAR,
        max: TARGET_PATH_LAST_YEAR,
      });
    }
  }

  return undefined;
};

type Props = {
  minKey: NumberInputsSearchParams;
  maxKey: NumberInputsSearchParams;
  title: string;
  unit?: UnitsValue;
  minFilter: number | null;
  maxFilter: number | null;
  isStrandingYearsFilter?: boolean;
};

const RangeFilter = ({ minKey, maxKey, title, unit, maxFilter, minFilter, isStrandingYearsFilter = false }: Props) => {
  const { setHasValidationError } = useBuildingFilters();

  const error = validateMinMax(isStrandingYearsFilter, minFilter, maxFilter);

  useEffect(() => {
    setHasValidationError(Boolean(error));
  }, [error, setHasValidationError]);

  return (
    <Collapsible title={title}>
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item xs>
          <NumberFilterInput type={minKey} isError={Boolean(error)} unit={unit} />
        </Grid>
        <Grid item>
          <PTypography variant="body1" sx={{ mx: 1.5 }}>
            -
          </PTypography>
        </Grid>
        <Grid item xs>
          <NumberFilterInput type={maxKey} isError={Boolean(error)} unit={unit} />
        </Grid>
      </Grid>
      <FormHelperText error>{error}</FormHelperText>
    </Collapsible>
  );
};

export default memo(RangeFilter, isEqual);
